import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Lock, HeartHandshake, Users } from "lucide-react";
import FadeIn from "@/components/FadeIn";
import SkylineDivider from "@/components/SkylineDivider";
import { IMAGES } from "@/lib/images";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ShadowFoundation() {
  const location = useLocation();
  useEffect(() => {
    if (location.pathname.endsWith("/apply")) {
      setTimeout(() => document.getElementById("apply")?.scrollIntoView({ behavior: "smooth", block: "start" }), 400);
    }
  }, [location.pathname]);
  const [form, setForm] = useState({
    applicant_type: "self",
    name: "",
    email: "",
    role: "",
    branch: "",
    season: "",
    preferred_timing: "",
    keep_anonymous: false,
  });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/foundation/apply`, form);
      toast.success("Application received. We'll read it personally and reach out with care.");
      setForm({
        applicant_type: "self",
        name: "",
        email: "",
        role: "",
        branch: "",
        season: "",
        preferred_timing: "",
        keep_anonymous: false,
      });
    } catch (err) {
      toast.error("Something went wrong. Please try again, or reach out by email.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="shadow-page">
      {/* HERO — Flag mask + Pennsylvania patch (patriotic signature) */}
      <section className="relative -mt-20 pt-32 lg:pt-44 pb-24 lg:pb-32 bg-flagNavyDeep text-cream overflow-hidden" data-testid="shadow-hero">
        <div className="absolute inset-0 opacity-90">
          <img src="/photos/shadow/shadow-ops-4.webp" alt="" className="w-full h-full object-cover object-[center_25%]" />
          <div className="absolute inset-0 bg-gradient-to-r from-flagNavyDeep/80 via-flagNavyDeep/30 to-flagNavyDeep/30" />
          <div className="absolute inset-0 bg-gradient-to-b from-flagNavyDeep/20 via-transparent to-flagNavyDeep/70" />
        </div>
        {/* Thin weathered flag stripe at the very top edge */}
        <div className="absolute top-0 left-0 right-0 h-[3px] flex opacity-80">
          <div className="flex-1 bg-flagRed" />
          <div className="flex-1 bg-cream/70" />
          <div className="flex-1 bg-flagNavy" />
        </div>
        <div className="relative container-x">
          <FadeIn>
            <img
              src="/shadow-foundation-logo-gold.png"
              alt="The Shadow Foundation"
              className="h-24 lg:h-32 w-auto mb-8 opacity-95 drop-shadow-[0_4px_12px_rgba(0,0,0,0.6)]"
            />
            <p className="overline text-cream/50 mb-6">A 501(c)(3) nonprofit</p>
          </FadeIn>
          <FadeIn delay={120}>
            <h1 className="font-serif text-3xl lg:text-[52px] font-light leading-[1.2] tracking-tight max-w-4xl">
              "People sleep peaceably in their beds at night{" "}
              <br className="hidden lg:block" />
              <em className="italic text-cream/75">only because rough men stand ready to do violence on their behalf."</em>
            </h1>
            <p className="mt-5 overline text-cream/40">— George Orwell</p>
          </FadeIn>
          <FadeIn delay={260}>
            <p className="mt-10 max-w-2xl text-cream/80 text-lg lg:text-xl leading-[1.85] font-light italic">
              For those who carry the weight so others do not have to.
            </p>
          </FadeIn>
          <FadeIn delay={400}>
            <div className="mt-12 flex flex-wrap gap-4">
              <a href="#apply" className="btn-ghost-light" data-testid="shadow-apply-cta">Apply for a stay</a>
              <Link to="/donate" className="self-center text-cream/70 hover:text-cream link-underline text-[13px] tracking-wide" data-testid="shadow-donate-cta">Or help fund one →</Link>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* SCRIPTURE */}
      <section className="container-narrow py-24 lg:py-32 text-center" data-testid="shadow-verse">
        <FadeIn>
          <p className="font-serif italic text-3xl lg:text-5xl text-flagNavy font-light leading-[1.25] tracking-tight">
            "He shall cover thee with his feathers,
            <br />
            and under his wings shalt thou trust."
          </p>
        </FadeIn>
        <FadeIn delay={180}>
          <p className="mt-10 overline">— Psalm 91:4</p>
        </FadeIn>
      </section>

      <SkylineDivider variant="moss" height={90} opacity={0.3} className="bg-cream hidden" data-testid="shadow-skyline-divider" />

      {/* ABOUT THE MISSION — weathered navy w/ silhouette operators behind */}
      <section className="bg-flagNavy text-cream py-24 lg:py-32 relative overflow-hidden" data-testid="shadow-about-mission">
        {/* Shadow-ops silhouette on the right, heavily faded */}
        {/* Gas-mask duo — fully kitted shadow-ops silhouette */}
        <div className="absolute inset-y-0 right-0 w-full lg:w-3/4 opacity-85 pointer-events-none">
          <img src="/photos/shadow/shadow-ops-3.webp" alt="" className="w-full h-full object-cover object-[25%_30%]" />
          <div className="absolute inset-0 bg-gradient-to-r from-flagNavy via-flagNavy/40 to-flagNavy/15" />
          <div className="absolute inset-0 bg-gradient-to-b from-flagNavy/20 via-transparent to-flagNavy/60" />
        </div>
        {/* Thin flag stripe divider at top */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-flagRed/50 to-transparent" />
        <div className="container-narrow relative">
          <FadeIn>
            <p className="overline text-cream/50 mb-5">About the Mission</p>
            <h2 className="font-serif text-3xl lg:text-5xl font-light leading-[1.15] tracking-tight max-w-3xl">
              The weight others rarely see.
            </h2>
          </FadeIn>
          <FadeIn delay={140}>
            <p className="mt-10 text-cream/80 text-lg leading-[1.9] max-w-3xl">
              Most people never see the weight carried by those who protect them. Military members,
              law enforcement officers, and first responders are trained to stay ready, stay sharp,
              and keep going. The Shadow Foundation exists for the moments after the call,
              after the shift, after the deployment, and after the noise.
            </p>
          </FadeIn>
          <FadeIn delay={260}>
            <p className="mt-8 text-cream/80 text-lg leading-[1.9] max-w-3xl">
              Through The Shadow Foundation, these men and women — and the families who wait up
              for them — are invited to Iron Talon Farm for stays of <em className="italic text-cream/95">respite, reprieve, and regeneration</em>.
              The whole property is theirs: every cottage, every trail, the pond, the fire pit,
              the long quiet. They come at no cost, anonymously if they want to, and they leave
              when they're ready.
            </p>
          </FadeIn>
          <FadeIn delay={380}>
            <p className="mt-8 text-cream/80 text-lg leading-[1.9] max-w-3xl">
              While the world rests easily because of them, we want them to know they are
              <em className="italic text-cream/95"> seen</em>. Honored. Welcomed. The strength and
              courage it takes to stand watch deserves more than a thank-you — it deserves a place
              to lay it down, even for a weekend, and be cared for in return.
            </p>
          </FadeIn>
          <FadeIn delay={500}>
            <div className="mt-14 border-t border-cream/15 pt-10 max-w-3xl">
              <p className="font-serif italic text-2xl lg:text-3xl text-cream/85 leading-[1.45]">
                "Greater love hath no man than this,
                <br />
                that a man lay down his life for his friends."
              </p>
              <p className="mt-5 overline text-cream/40">— John 15:13</p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* PRINCIPLES */}
      <section className="bg-flagNavyDeep text-cream py-28 lg:py-40 relative overflow-hidden" data-testid="shadow-principles">
        <div className="absolute inset-0 pointer-events-none" data-testid="shadow-principles-flag">
          <img src="/photos/shadow/vintage-flag.webp" alt="" aria-hidden="true" className="w-full h-full object-cover object-left-top select-none" />
          <div className="absolute inset-0 bg-[#2a1a10]/40" />
          <div className="absolute inset-0 bg-gradient-to-b from-flagNavyDeep/40 via-transparent to-flagNavyDeep/60" />
        </div>
        <div className="relative">
        <div className="container-x">
          <FadeIn>
            <p className="overline text-cream/70 mb-5">How We Hold Space</p>
            <h2 className="font-serif text-4xl lg:text-5xl text-cream font-light leading-tight tracking-tight max-w-2xl [text-shadow:0_2px_18px_rgba(0,0,0,0.5)]">
              Quiet. Respectful. On their terms.
            </h2>
          </FadeIn>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-12 lg:gap-16">
            {[
              { Icon: Lock, title: "Private & Anonymous", desc: "Guests can step out of the spotlight, off the schedule, and into quiet. No public lists. No public thank-yous unless they choose." },
              { Icon: HeartHandshake, title: "No Cost to Recipients", desc: "Donations and proceeds from Iron Talon's cottage rentals fund stays for those who need it most. Recipients pay nothing." },
              { Icon: Users, title: "For Families, Too", desc: "Whether it's an individual, a couple, or a family that carries the weight together — the farm holds space for the whole picture." },
            ].map(({ Icon, title, desc }, i) => (
              <FadeIn key={title} delay={i * 140}>
                <div className="border-t border-cream/40 pt-6 bg-flagNavyDeep/60 backdrop-blur-sm px-6 pb-8">
                  <Icon strokeWidth={1.3} size={28} className="text-cream" />
                  <h3 className="mt-5 font-serif text-2xl text-cream font-medium">{title}</h3>
                  <p className="mt-3 text-cream/85 leading-[1.85]">{desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
        </div>
      </section>

      {/* APPLY FORM */}
      <section id="apply" className="py-24 lg:py-32 scroll-mt-24" data-testid="shadow-apply-section">
        <div className="container-narrow">
          <FadeIn>
            <p className="overline mb-5 text-center">Apply for a Stay</p>
            <h2 className="font-serif text-4xl lg:text-6xl text-flagNavy font-light leading-[1.05] tracking-tight text-center">
              Apply for a quiet stay.
            </h2>
            <p className="mt-6 text-coal/70 text-lg leading-[1.85] text-center max-w-2xl mx-auto">
              Whether for yourself or someone you love, share a little of the story. Every application
              is read personally and kept confidential. We do not require proof of service or hardship —
              we do ask for honesty so we can steward gifts well.
            </p>
          </FadeIn>

          <FadeIn delay={180}>
            <form onSubmit={onSubmit} className="mt-14 space-y-10" data-testid="foundation-apply-form">
              <div>
                <label className="label-clean">This is for</label>
                <div className="flex gap-3 mt-2">
                  {[
                    { value: "self", label: "Myself" },
                    { value: "other", label: "Someone I know" },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, applicant_type: opt.value }))}
                      data-testid={`apply-type-${opt.value}`}
                      className={`px-6 py-3 text-sm tracking-wide border transition-all duration-500 ${
                        form.applicant_type === opt.value
                          ? "bg-flagNavy text-cream border-flagNavy"
                          : "bg-transparent text-flagNavy border-flagNavy/30 hover:border-flagNavy"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                <div>
                  <label className="label-clean">Your name (or "Anonymous")</label>
                  <input required name="name" value={form.name} onChange={onChange} className="input-clean" data-testid="apply-name-input" />
                </div>
                <div>
                  <label className="label-clean">Contact email</label>
                  <input required type="email" name="email" value={form.email} onChange={onChange} className="input-clean" data-testid="apply-email-input" />
                </div>
                <div>
                  <label className="label-clean">Role / service</label>
                  <select name="role" value={form.role} onChange={onChange} className="input-clean" data-testid="apply-role-select">
                    <option value="">Select…</option>
                    <option>Military</option>
                    <option>First responder</option>
                    <option>Law enforcement</option>
                    <option>Helping professional</option>
                    <option>Other</option>
                  </select>
                </div>
                <div>
                  <label className="label-clean">Branch or department (optional)</label>
                  <input name="branch" value={form.branch} onChange={onChange} className="input-clean" data-testid="apply-branch-input" />
                </div>
                <div className="md:col-span-2">
                  <label className="label-clean">A little about the season they're in</label>
                  <textarea required name="season" rows={5} value={form.season} onChange={onChange} className="input-clean resize-none" placeholder="No need for polish or detail — just honesty about what's heavy and what kind of rest might help." data-testid="apply-season-input" />
                </div>
                <div className="md:col-span-2">
                  <label className="label-clean">Preferred timing (optional)</label>
                  <input name="preferred_timing" value={form.preferred_timing} onChange={onChange} className="input-clean" placeholder="A weekend in early fall, sometime next month, no rush, etc." data-testid="apply-timing-input" />
                </div>
                <div className="md:col-span-2 flex items-start gap-3">
                  <input
                    type="checkbox"
                    name="keep_anonymous"
                    id="keep_anonymous"
                    checked={form.keep_anonymous}
                    onChange={onChange}
                    className="mt-1.5 accent-moss"
                    data-testid="apply-anonymous-checkbox"
                  />
                  <label htmlFor="keep_anonymous" className="text-coal/70 text-sm leading-[1.7]">
                    Keep this application anonymous. We won't share publicly or use names without permission.
                  </label>
                </div>
              </div>

              <button type="submit" disabled={loading} className="bg-flagNavy text-cream px-8 py-3 text-xs tracking-[0.28em] uppercase hover:bg-flagNavyDeep transition-colors disabled:opacity-50" data-testid="apply-submit-btn">
                {loading ? "Sending…" : "Submit application"}
              </button>
            </form>
          </FadeIn>
        </div>
      </section>

      {/* PSALM 27 — closing scripture */}
      <section className="bg-cream py-24 lg:py-32" data-testid="shadow-closing-psalm">
        <div className="container-narrow text-center">
          <FadeIn>
            <p className="overline mb-6">A Closing Psalm</p>
            <p className="font-serif italic text-2xl lg:text-4xl text-flagNavy font-light leading-[1.4] tracking-tight max-w-3xl mx-auto">
              "The Lord is my light and my salvation;
              <br />
              whom shall I fear?"
            </p>
            <p className="mt-8 overline">— Psalm 27:1</p>
          </FadeIn>
        </div>
      </section>

      {/* DONATE CTA — weathered flag red/brick like a worn barn */}
      <section className="bg-flagRed text-cream py-24 lg:py-32 relative overflow-hidden" data-testid="shadow-donate-section">
        {/* Street-scene duo w/ barriers — ready-to-serve */}
        <div className="absolute inset-0 opacity-60 pointer-events-none">
          <img src="/photos/shadow/shadow-ops-1.webp" alt="" className="w-full h-full object-cover object-[center_30%]" />
          <div className="absolute inset-0 bg-gradient-to-b from-flagRed/35 via-flagRed/20 to-flagRed/70" />
          <div className="absolute inset-0 bg-gradient-to-r from-flagRed/60 via-flagRed/5 to-flagRed/60" />
        </div>
        {/* Flag stripe accent */}
        <div className="absolute top-0 left-0 right-0 h-[2px] flex opacity-60">
          <div className="flex-1 bg-cream/60" />
          <div className="flex-1 bg-flagNavy" />
          <div className="flex-1 bg-cream/60" />
        </div>
        <div className="container-narrow text-center relative">
          <FadeIn>
            <p className="overline text-cream/60 mb-5">Help Fund a Stay</p>
            <h2 className="font-serif text-4xl lg:text-6xl font-light leading-[1.05] tracking-tight">
              Donate a day of rest.
            </h2>
            <p className="mt-8 text-cream/75 text-lg leading-[1.85] max-w-xl mx-auto">
              Every dollar quietly underwrites a stay for someone who carries the weight others rarely see.
              A single weekend can change the trajectory of someone's year.
            </p>
            <Link to="/donate" className="mt-10 inline-flex btn-ghost-light" data-testid="shadow-cta-donate-btn">
              Make a donation
            </Link>
            <p className="mt-8 text-cream/55 text-xs leading-[1.85] max-w-md mx-auto">
              The Shadow Foundation is a registered 501(c)(3) nonprofit organization.
              Your donation is tax-deductible to the fullest extent allowed by law.
            </p>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
